import React, { useState } from 'react';
import { Modal, Form, Input, Button, Typography, DatePicker } from 'antd';
import moment, { Moment } from 'moment';
// Types
import { GetCampaignOffers_getCampaignOffers_entities_deliverablesV2 } from 'api/campaignV2/types/GetCampaignOffers';
import { TaskExtensionRequestType } from 'api/graphql-global-types';
// Constants
import { shortDateFormat } from 'constants/global';
// Helpers
import { getAppearanceDateValue } from 'helpers/deliverables';
// Hooks
import { useTaskExtensions } from 'hooks/useTaskExtensions';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
// Components
import ExtensionHistory from '../ExtensionHistory/ExtensionHistory';
// Styles
import styles from './RequestExtensionModal.module.scss';

const { Text } = Typography;
const { TextArea } = Input;

type RequestExtensionModalProps = {
  extensionType: TaskExtensionRequestType;
  isVisible: boolean;
  onClose: () => void;
  deliverable: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2;
  onSuccess: () => void;
  offerId: string;
  getCbTaskExtensionHistory: (options: any) => void;
  extensionHistoryData?: any[];
  extensionHistoryLoading?: boolean;
};

const RequestExtensionModal: React.FC<RequestExtensionModalProps> = ({
  extensionType,
  isVisible,
  onClose,
  deliverable,
  onSuccess,
  offerId,
  getCbTaskExtensionHistory,
  extensionHistoryData,
  extensionHistoryLoading,
}) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { requestExtension } = useTaskExtensions();

  const isEventExtension = extensionType === TaskExtensionRequestType.EVENT;

  const currentDueDate = isEventExtension
    ? getAppearanceDateValue(deliverable)
    : extensionType === TaskExtensionRequestType.CONTENT
    ? deliverable?.contentDueDateUtc
    : deliverable?.publishDueDateUtc;

  const disabledDate = (current: Moment) => {
    return current && current <= moment().endOf('day');
  };

  const handleSubmit = async (values: {
    extensionDate: Moment;
    reason: string;
  }) => {
    setIsSubmitting(true);
    try {
      const deliverableId = deliverable?.id;
      if (!deliverableId) {
        errorNotification('Invalid deliverable ID format');
        return;
      }

      // Appearance is a fixed date/time — send the new event datetime instead
      // of a day delta so the scheduled moment is preserved.
      if (isEventExtension) {
        await requestExtension({
          variables: {
            input: {
              deliverableId,
              reason: values.reason,
              eventDate: values.extensionDate.toISOString(),
              type: extensionType,
            },
          },
        });

        successNotification('Extension request submitted successfully');
        form.resetFields();

        onSuccess();
        onClose();
        return;
      }

      const currentDeadline = moment(currentDueDate).startOf('day');
      const requestedExtensionDays = values.extensionDate
        .startOf('day')
        .diff(currentDeadline, 'days');

      await requestExtension({
        variables: {
          input: {
            deliverableId,
            reason: values.reason,
            requestedExtensionDays,
            type: extensionType,
          },
        },
      });

      successNotification('Extension request submitted successfully');
      form.resetFields();

      onSuccess();
      onClose();
    } catch (error: any) {
      errorNotification(
        error?.message || 'Something went wrong, please try again later'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="Request Extension"
      visible={isVisible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <div className={styles.root}>
        <div className={styles.description}>
          <Text>
            {isEventExtension
              ? 'Select a new date for the appearance. Current date: '
              : 'Select a specific date to extend the deadline to. Current deadline: '}
            <strong>
              {currentDueDate
                ? moment(currentDueDate).format(
                    isEventExtension ? 'MMMM D, YYYY, hh:mm A' : shortDateFormat
                  )
                : 'N/A'}
            </strong>
          </Text>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className={styles.form}
        >
          <Form.Item
            name="extensionDate"
            label={
              isEventExtension ? 'New Appearance Date' : 'Extend Deadline To'
            }
            rules={[
              {
                required: true,
                message: isEventExtension
                  ? 'Please select a new appearance date'
                  : 'Please select a new deadline date',
              },
            ]}
          >
            <DatePicker
              style={{ width: '100%' }}
              disabledDate={disabledDate}
              showTime={isEventExtension}
              format={isEventExtension ? 'MM/DD/YYYY h:mm a' : shortDateFormat}
              placeholder={
                isEventExtension
                  ? 'Select new appearance date'
                  : 'Select new deadline date'
              }
            />
          </Form.Item>

          <Form.Item
            name="reason"
            label="Reason For Extension"
            rules={[
              {
                required: true,
                message: 'Please provide a reason for extension',
              },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Explain to the brand why you are in need of an extension so they can consider for their approval"
            />
          </Form.Item>

          <div className={styles.actions}>
            <Button onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={isSubmitting}>
              Submit Request
            </Button>
          </div>
        </Form>

        <ExtensionHistory
          deliverableId={deliverable?.id || ''}
          offerId={offerId}
          getCbTaskExtensionHistory={getCbTaskExtensionHistory}
          extensionHistoryData={extensionHistoryData}
          extensionHistoryLoading={extensionHistoryLoading}
        />
      </div>
    </Modal>
  );
};

export default RequestExtensionModal;
